import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

function CountUp({ value }) {
    const match = value.match(/^(\d+)(.*)$/);
    const [display, setDisplay] = useState(match ? "0" + match[2] : value);
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });

    useEffect(() => {
        if (!inView || !match) return;
        const target = parseInt(match[1], 10);
        const suffix = match[2];
        const start = performance.now();
        const dur = 1400;
        let raf;
        const tick = (now) => {
            const p = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(target * eased) + suffix);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, value]);

    return <span ref={ref}>{display}</span>;
}

export default function HeroSection({ t, onNavigate }) {
    return (
        <section id="hero" className="relative border-b border-white/5">
            <div className="pointer-events-none absolute inset-0 bg-grid bg-[radial-gradient(ellipse_at_top,rgba(74,222,128,0.06),transparent_60%)]" />
            <div className="pointer-events-none absolute right-[10%] top-[24%] hidden lg:block">
                <div className="h-24 w-24 border border-glacier/30 animate-floaty" />
            </div>
            <div className="pointer-events-none absolute right-[24%] top-[58%] hidden lg:block">
                <div className="h-10 w-10 rounded-full border border-ivory/20 animate-floaty-slow" />
            </div>
            <div className="pointer-events-none absolute right-[32%] top-[30%] hidden lg:block">
                <div className="h-4 w-4 rotate-45 bg-glacier/30 animate-floaty" />
            </div>
            <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-36 sm:pt-44 pb-20 sm:pb-28">
                <motion.div {...fadeUp(0.05)} className="mb-7 flex justify-center">
                    <span
                        data-testid="hero-availability-badge"
                        className="inline-flex items-center gap-2.5 rounded-full border border-glacier/30 bg-glacier/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-glacier"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-glacier opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-glacier" />
                        </span>
                        {t.hero.badge}
                    </span>
                </motion.div>

                <motion.p
                    {...fadeUp(0.1)}
                    className="flex items-center justify-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-glacier"
                >
                    <span className="inline-block h-px w-10 bg-glacier/60" />
                    {t.hero.overline}
                    <span className="inline-block h-px w-10 bg-glacier/60" />
                </motion.p>

                <h1
                    data-testid="hero-main-title"
                    className="mt-8 text-center font-display font-medium leading-[1.05] tracking-tight text-[clamp(3rem,8.5vw,6.75rem)]"
                >
                    {t.hero.lines.map((line, i) => (
                        <motion.span
                            key={i}
                            {...fadeUp(0.25 + i * 0.12)}
                            className={`block ${i === t.hero.accentLine ? "text-glacier glow-white" : "glow-green"}`}
                        >
                            {line}
                        </motion.span>
                    ))}
                </h1>

                <motion.p {...fadeUp(0.75)} className="mt-8 mx-auto max-w-2xl text-center text-lg sm:text-xl leading-relaxed font-medium text-ivory glow-green-soft">
                    {t.hero.sub}
                </motion.p>

                <motion.div {...fadeUp(0.9)} className="mt-10 flex flex-wrap items-center justify-center gap-4">
                    <button
                        data-testid="hero-cta-request-quote"
                        onClick={() => onNavigate("#kontakt")}
                        className="group inline-flex items-center gap-3 rounded-sm bg-glacier btn-shine px-8 py-4 font-display text-sm font-semibold uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-ivory hover:shadow-[0_0_28px_rgba(74,222,128,0.28)]"
                    >
                        {t.hero.ctaPrimary}
                        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button
                        data-testid="hero-cta-explore-work"
                        onClick={() => onNavigate("#portfolio")}
                        className="inline-flex items-center gap-3 rounded-sm border border-white/20 px-8 py-4 font-display text-sm font-semibold uppercase tracking-widest text-ivory transition-[border-color,color] duration-300 hover:border-glacier hover:text-glacier"
                    >
                        {t.hero.ctaSecondary}
                        <ArrowDown size={15} />
                    </button>
                </motion.div>

                <motion.div
                    {...fadeUp(1.05)}
                    className="mt-20 mx-auto max-w-2xl grid grid-cols-2 gap-6 border-t border-white/5 pt-8 text-center"
                >
                    {t.hero.stats.map((s, i) => (
                        <div key={i} data-testid={`hero-stat-${i}`}>
                            <div className="font-display text-3xl sm:text-4xl font-medium text-ivory">
                                <CountUp value={s.value} />
                            </div>
                            <div className="mt-1.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-fog">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
